import type {
	ChangeEventHandler,
	InputHTMLAttributes,
	JSX,
	KeyboardEventHandler,
} from 'react';

import * as palette from 'pkg/config/palette';

import { cssClasses } from 'pkg/css/utils';
import { debounce } from 'pkg/timings';
import withForwardedRef from 'pkg/withForwardedRef';

import Row from 'components/layout/row';

import Button from 'design/button';

import * as css from './styles.css';

const NOOP = () => {};

interface ForwardedControllerProps extends InputHTMLAttributes<HTMLInputElement> {
	ref: any;
}

interface RangeSliderProps {
	min: number;
	max: number;
	step: number;
	value: number;
	thumbColor: string;
	trackColor: string;
	progressColor: string;
	withButtons: boolean;
	disabled?: boolean;
	className?: string;
	forwardedRef?: any;

	onChange: (value: number, progress: number) => void;
	onBeforeChange: () => void;
	onAfterChange: () => void;

	thumbSize: 'small' | 'medium' | 'large';
}

export const RangeSlider = withForwardedRef(
	({
		min = 0,
		max = 100,
		step = 1,
		thumbSize = 'medium',
		thumbColor = palette.white,
		trackColor = palette.gray[300],
		progressColor = palette.blue[500],
		withButtons,
		disabled,
		className,
		forwardedRef,
		value,
		onBeforeChange = NOOP,
		onAfterChange = NOOP,
		onChange,
	}: RangeSliderProps): JSX.Element => {
		const calculateProgress = (value: number) => {
			if (value > max) value = max;

			if (value < min) value = min;

			let progress = ((value - min) * 100) / (max - min);

			if (Number.isNaN(progress)) {
				progress = 0;
			}

			return progress;
		};

		const handleControllerChange: ChangeEventHandler<HTMLInputElement> = (
			event
		) => {
			const value = Number.parseFloat(event.currentTarget.value);
			const progress = calculateProgress(value);

			onChange(value, progress);
			debouncedChangeHandler();
		};

		const handleIncrementClick = () => {
			const newValue = value + step;
			const progress = calculateProgress(newValue);

			onChange(newValue, progress);
		};

		const handleDecrementClick = () => {
			const newValue = value - step;
			const progress = calculateProgress(newValue);

			onChange(newValue, progress);
		};

		const handleInputStart = () => onBeforeChange();

		const disableUserKeyInput: KeyboardEventHandler<HTMLInputElement> = (
			event
		) => event.preventDefault();

		const debouncedChangeHandler = debounce(() => {
			onAfterChange();
		}, 300);

		const progresPercentage = () => {
			const progress = calculateProgress(value);

			return `${progress}%`;
		};

		const controllerProperties: ForwardedControllerProps = {
			type: 'range',
			min,
			max,
			step,
			ref: forwardedRef,
			value,
			disabled,
			tabIndex: -1,
			onChange: handleControllerChange,
			onMouseDown: handleInputStart,
			onKeyDown: disableUserKeyInput,
		};

		const rangeProperties = {
			x1: '100%',
			y1: '50%',
			x2: '0%',
			y2: '50%',
			stroke: trackColor,
		};

		const progressProperties = {
			x1: progresPercentage(),
			y1: '50%',
			x2: '0%',
			y2: '50%',
			stroke: progressColor,
		};

		const thumbSizes = {
			small: 6,
			medium: 10,
			large: 16,
		};

		const thumbProperties = {
			cx: progresPercentage(),
			cy: '50%',
			fill: thumbColor,
			r: thumbSizes[thumbSize],
		};

		const content = (
			<div
				className={cssClasses(css.wrapper, className && className)}
				data-testid="range_slider">
				<input {...controllerProperties} className={css.controller} />
				<svg className={css.slider}>
					<g>
						<line {...rangeProperties} className={css.line} />
						<line {...progressProperties} className={css.line} />
					</g>
					<circle {...thumbProperties} className={css.thumb} />
				</svg>
			</div>
		);

		if (withButtons) {
			return (
				<Row columns="30px 1fr 30px" align="center" justify="center">
					<Button
						icon="remove"
						iconSize={1}
						disabled={disabled}
						onClick={handleDecrementClick}
						className={css.sliderButton}
						data-testid="range_slider.decrement_btn"
					/>
					{content}
					<Button
						icon="add"
						iconSize={1}
						disabled={disabled}
						onClick={handleIncrementClick}
						className={css.sliderButton}
						data-testid="range_slider.increment_btn"
					/>
				</Row>
			);
		}

		return content;
	}
);
